import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Archive,
  ArrowLeft,
  Calendar,
  ChevronDown,
  Clock,
  IndianRupee,
  MapPin,
  Mic,
  RotateCcw,
  SendHorizontal,
  ShieldCheck,
} from "lucide-react";
import { useExpressChatRoom } from "@/hooks/useExpressChatRoom";
import { formatLastSeenLabel, getChatMessageDisplayText, maskChatPersonalInfo } from "@/services/chat/firebaseChat";
import {
  getChatThread,
  listChatThreads,
  normalizeBookingId,
  sendArtistChatMessageHttp,
} from "@/services/chat/chatApi";
import { useBookingFormsStore } from "@/stores/bookingFormsStore";
import type { ChatFirebasePayload, ChatThreadDetail, ExpressChatMessage } from "@/types/expressChat";

const TypingDots = () => (
  <span className="flex items-center gap-1 px-1" aria-hidden>
    {[0, 1, 2].map((index) => (
      <span
        key={index}
        className="h-1.5 w-1.5 rounded-full bg-[#A0A0A0]"
        style={{
          animation: "express-chat-typing 1.05s ease-in-out infinite",
          animationDelay: `${index * 0.16}s`,
        }}
      />
    ))}
  </span>
);

const PresenceStatus = ({
  isOnline,
  lastSeenAt,
}: {
  isOnline: boolean;
  lastSeenAt: number | null;
}) => {
  if (isOnline) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4ADE80]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
        Online
      </span>
    );
  }

  const lastSeen = formatLastSeenLabel(lastSeenAt);
  if (lastSeen) {
    return <span className="text-[12px] font-normal leading-tight text-[#8E8E93]">{lastSeen}</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#8E8E93]">
      <span className="h-1.5 w-1.5 rounded-full bg-[#8E8E8E]" />
      Offline
    </span>
  );
};

export const ArtistChatPage: React.FC = () => {
  const { bookingId = "" } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const submitted = useBookingFormsStore((s) => s.submitted);
  const ensureFetched = useBookingFormsStore((s) => s.ensureFetched);

  useEffect(() => {
    void ensureFetched();
  }, [ensureFetched]);
  const [thread, setThread] = useState<ChatThreadDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [accessNonce, setAccessNonce] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [failedMessages, setFailedMessages] = useState<ExpressChatMessage[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  const matchingForm = useMemo(
    () =>
      submitted.find(
        (form) =>
          normalizeBookingId(form.express_booking_code) ===
          normalizeBookingId(bookingId),
      ),
    [bookingId, submitted],
  );

  const loadThread = useCallback(async () => {
    try {
      const threads = await listChatThreads();
      const match = threads.find(
        (item) =>
          normalizeBookingId(item.booking_id) === normalizeBookingId(bookingId),
      );
      if (!match) {
        setLoadError("Chat not available");
        return;
      }
      const detail = await getChatThread(match.id);
      setThread(detail);
      setLoadError(null);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Unable to open chat",
      );
    }
  }, [bookingId]);

  useEffect(() => {
    void loadThread();
  }, [accessNonce, loadThread]);

  const firebase: ChatFirebasePayload | null = thread?.firebase ?? null;
  const canSend = Boolean(thread?.can_send && thread?.chat_unlocked !== false);

  const httpSend = useCallback(
    async (text: string) => {
      if (!thread?.id) return;
      await sendArtistChatMessageHttp(thread.id, text);
    },
    [thread?.id],
  );

  const {
    messages,
    isPeerOnline,
    peerLastSeenAt,
    isPeerTyping,
    isConnecting,
    error,
    send,
    setTypingFromDraft,
  } = useExpressChatRoom(firebase, {
    enabled: Boolean(firebase),
    canSend,
    httpSend,
    onPermissionDenied: () => setAccessNonce((value) => value + 1),
  });

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isPeerTyping, failedMessages]);

  const clientName = thread?.client_full_name?.trim() || "Client";
  const clientFirstName = clientName.split(/\s+/)[0] || "Client";
  const eventType =
    matchingForm?.event_type || thread?.event_type || "Booking";
  const duration =
    matchingForm?.event_duration_mins ||
    matchingForm?.performance_duration_mins ||
    thread?.duration_mins;
  const fee = matchingForm?.fee || thread?.fee;
  const city =
    matchingForm?.venue_city_state ||
    matchingForm?.venue ||
    thread?.venue_city ||
    "";
  const eventDate = matchingForm?.event_date || thread?.event_date || "";
  const eventTime = matchingForm?.event_time || thread?.event_time || "";
  const bookingIdLabel = bookingId.replace(/^EB-?/i, "EB-");

  const threadMessages = useMemo(
    () =>
      [...messages, ...failedMessages].sort(
        (a, b) => a.createdAtMs - b.createdAtMs,
      ),
    [failedMessages, messages],
  );

  const handleSend = () => {
    const text = draft.trim();
    if (!text || !canSend) return;
    setDraft("");
    setTypingFromDraft("");
    void send(text).catch(() => {
      setFailedMessages((current) => [
        ...current,
        {
          id: `failed-${Date.now()}`,
          side: "outgoing",
          text,
          timestamp: "",
          dateLabel: "TODAY",
          createdAtMs: Date.now(),
          isMasked: maskChatPersonalInfo(text).wasMasked,
          status: "failed",
        },
      ]);
    });
  };

  const handleRetry = (message: ExpressChatMessage) => {
    if (!canSend) return;
    setFailedMessages((current) => current.filter((item) => item.id !== message.id));
    void send(message.text).catch(() => {
      setFailedMessages((current) => [
        ...current,
        { ...message, createdAtMs: Date.now() },
      ]);
    });
  };

  if (loadError) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-sm text-[#8E8E93]">{loadError}</p>
        <button
          type="button"
          className="text-sm font-semibold text-[#F33363]"
          onClick={() => navigate("/jobs", { state: { tab: "express" } })}
        >
          Back to bookings
        </button>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center text-sm text-[#8E8E93]">
        Opening chat…
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-[calc(var(--app-height,100dvh)-5.5rem)] w-full max-w-lg flex-col bg-black text-white">
      <style>
        {`@keyframes express-chat-typing {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }`}
      </style>
      <header className="flex items-center justify-between gap-3 px-3 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/jobs", { state: { tab: "express" } })}
            className="shrink-0 rounded-lg p-1 text-white"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-[17px] font-bold leading-tight text-white">
              {clientName}
            </h1>
            <PresenceStatus isOnline={isPeerOnline} lastSeenAt={peerLastSeenAt} />
          </div>
        </div>
        <p className="shrink-0 text-xs font-normal text-[#8E8E93]">{bookingIdLabel}</p>
      </header>

      <div className="mx-3 rounded-2xl bg-[#1C1C1E] px-3 py-2.5">
        <button
          type="button"
          onClick={() => setBookingOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-2 text-left"
          aria-expanded={bookingOpen}
        >
          <p className="truncate text-sm font-semibold">{eventType} Booking</p>
          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-[#C7C7CC]">
            {eventDate || "Date TBD"}
            <ChevronDown
              className={`h-4 w-4 text-[#E8C36A] transition-transform ${
                bookingOpen ? "rotate-180" : ""
              }`}
            />
          </span>
        </button>
        {bookingOpen ? (
          <div className="mt-3 space-y-1.5 text-xs text-[#C7C7CC]">
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-full border border-[#F33363]/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#F33363]">
                {canSend ? "Confirmed" : "Completed"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-[#F33363]" />
              <span>
                {eventDate}
                {eventTime ? ` · ${eventTime}` : ""}
              </span>
            </div>
            {city ? (
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-[#F33363]" />
                <span className="truncate">{city}</span>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              {duration ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-[#F33363]" />
                  {duration} min
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1">
                <Mic className="h-3.5 w-3.5 text-[#F33363]" />
                Singer
              </span>
              {fee ? (
                <span className="inline-flex items-center gap-1">
                  <IndianRupee className="h-3.5 w-3.5 text-[#F33363]" />
                  {fee}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="mt-1 text-xs font-semibold text-[#E8C36A]"
              onClick={() =>
                navigate("/jobs", {
                  state: {
                    tab: "express",
                    spotlightBookingDealId: matchingForm?.deal_id,
                  },
                })
              }
            >
              View Details
            </button>
          </div>
        ) : null}
      </div>

      <div className="mx-3 mt-3 flex items-center justify-center gap-2 rounded-xl bg-[#1C1C1E] px-3 py-2 text-[11px] text-[#8E8E93]">
        <ShieldCheck className="h-3.5 w-3.5" />
        Keep your booking communication and payments on StarClinch.
      </div>

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4">
        {isConnecting && threadMessages.length === 0 ? (
          <p className="text-center text-sm text-[#8E8E93]">Connecting…</p>
        ) : threadMessages.length === 0 && !isPeerTyping ? (
          <>
            <p className="mb-4 text-center text-[10px] font-semibold uppercase tracking-widest text-[#8E8E93]">
              Today
            </p>
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-[#8E8E93]">No messages yet</p>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4">
            {threadMessages.map((message, index) => {
              const showDate =
                index === 0 ||
                threadMessages[index - 1]?.dateLabel !== message.dateLabel;
              const isFailed = message.status === "failed";
              const isMaskedOutgoing =
                Boolean(message.isMasked) &&
                message.side === "outgoing" &&
                !isFailed;
              return (
                <div key={message.id}>
                  {showDate ? (
                    <p className="mb-4 text-center text-[10px] font-semibold uppercase tracking-widest text-[#8E8E93]">
                      {message.dateLabel}
                    </p>
                  ) : null}
                  <div
                    className={`flex max-w-[85%] flex-col ${
                      message.side === "outgoing" ? "ml-auto items-end" : "items-start"
                    }`}
                  >
                    <div
                      className={`break-all px-3.5 py-2.5 text-sm leading-snug text-white ${
                        message.side === "outgoing"
                          ? "rounded-2xl rounded-br-md bg-[#2C2C2E]"
                          : "rounded-2xl rounded-bl-md bg-[#3A3A3C]"
                      } ${
                        isFailed
                          ? "border border-[#F33363]"
                          : isMaskedOutgoing
                            ? "border border-[#E8C36A]"
                            : ""
                      }`}
                    >
                      {getChatMessageDisplayText(message.text)}
                    </div>
                    {isFailed ? (
                      <button
                        type="button"
                        onClick={() => handleRetry(message)}
                        className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#F33363]"
                      >
                        Message not sent. Retry
                        <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.25} />
                      </button>
                    ) : isMaskedOutgoing ? (
                      <span className="mt-1 text-right text-[11px] leading-snug">
                        <span className="block text-[#E8C36A]">
                        Sharing personal info is strictly prohibited for security reasons.
                        </span>
                        <span className="text-[#8E8E93]">{message.timestamp}</span>
                      </span>
                    ) : (
                      <span className="mt-1 text-[11px] text-[#8E8E93]">
                        {message.timestamp}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {isPeerTyping ? (
              <div className="inline-flex h-8 w-14 items-center justify-center rounded-full bg-[#3A3A3C]">
                <TypingDots />
              </div>
            ) : null}
          </div>
        )}
        {error ? <p className="mt-2 text-center text-[11px] text-red-400">{error}</p> : null}
      </div>

      {!canSend ? (
        <div className="mx-3 mb-3 flex items-center gap-2.5 rounded-xl border border-white/10 bg-[#1A1C22] px-3 py-2.5">
          <Archive className="h-4 w-4 shrink-0 text-[#A0A0A0]" />
          <p className="text-xs leading-snug text-[#A0A0A0]">
            This conversation is now read-only — the event has been completed.
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 pb-4">
          <input
            type="text"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setTypingFromDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            placeholder={`Message ${clientFirstName}...`}
            className="h-12 min-w-0 flex-1 rounded-full border border-white/10 bg-[#1C1C1E] px-4 text-sm text-white outline-none placeholder:text-[#8E8E93]"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim()}
            aria-label="Send message"
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
              draft.trim()
                ? "bg-[#F33363] text-white shadow-[0_0_16px_rgba(243,51,99,0.45)]"
                : "bg-[#2C2C2E] text-[#8E8E93]"
            }`}
          >
            <SendHorizontal className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};
